import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  Output,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { ZoneViewer } from 'src/app/model';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

interface FilterOption {
  id: string;
  label: string;
  value: any;
  checked?: boolean;
}

interface FilterGroup {
  type: 'dropdown' | 'checkbox';
  id: string;
  label: string;
  options: FilterOption[];
  expanded?: boolean;
}

@Component({
  selector: 'app-drawer-filter',
  templateUrl: './drawer-filter.component.html',
  styleUrl: './drawer-filter.component.scss',
  animations: [
    trigger('drawerAnimation', [
      state(
        'closed',
        style({
          transform: 'translateX(100%)',
        })
      ),
      state(
        'open',
        style({
          transform: 'translateX(0)',
        })
      ),
      transition('closed => open', animate('300ms ease-in-out')),
      transition('open => closed', animate('300ms ease-in-out')),
    ]),
    trigger('expandCollapse', [
      state(
        'collapsed',
        style({
          height: '0',
          overflow: 'hidden',
          opacity: 0,
        })
      ),
      state(
        'expanded',
        style({
          height: '*',
          opacity: 1,
        })
      ),
      transition('collapsed <=> expanded', animate('300ms ease-in-out')),
    ]),
  ],
})
export class DrawerFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() searchEnabled = true;
  @Input() searchPlaceholder = 'Search...';
  @Input() searchValue = '';
  @Input() appliedFilters: Record<string, any> = {};
  @Input() view: string;

  // Configuration for checkbox filters only
  @Input() checkboxFilterConfig: FilterGroup[] = [];

  // Final processed filter groups
  processedFilters: FilterGroup[] = [];

  @Output() close = new EventEmitter<{ isOpen: boolean }>();
  @Output() search = new EventEmitter<string>();
  @Output() clearAll = new EventEmitter<void>();
  @Output() filtersChanged = new EventEmitter<Record<string, any>>();

  filterForm: FormGroup;
  hasActiveFilters: boolean = false;
  private searchSubject = new Subject<string>();
  readonly ZONE_VIEWER = ZoneViewer;

  constructor(private fb: FormBuilder) {
    this.filterForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.processFilterConfigurations();
    this.initializeForm();
    this.checkForActiveFilters();
    this.searchSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((value) => {
        if (
          value !== null &&
          ((value.length && value?.substring(value.length - 1) !== ' ') ||
            !value.length)
        ) {
          this.search.emit(value);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Process filter configurations when inputs change
    if (changes?.['checkboxFilterConfig']) {
      this.processFilterConfigurations();
      this.initializeForm();
    }

    // Update form when applied filters change externally
    if (changes?.['appliedFilters'] && this.filterForm && this.appliedFilters) {
      this.updateFormFromAppliedFilters();
      this.checkForActiveFilters();
    }

    if (changes?.['view']) {
      this.clearAllFilters();
    }
  }

  ngOnDestroy(): void {
    this.searchSubject.complete();
  }

  /**
   * Check if any checkbox filter is currently selected
   */
  checkForActiveFilters(): void {
    if (!this.filterForm) {
      this.hasActiveFilters = false;
      return;
    }
    this.hasActiveFilters = false;
    this.processedFilters.forEach((group) => {
      const groupControl = this.filterForm.get(group.id) as FormGroup;
      if (!groupControl) return;
      Object.keys(groupControl.controls).forEach((controlKey) => {
        if (groupControl.get(controlKey)?.value === true) {
          this.hasActiveFilters = true;
        }
      });
    });
  }

  /**
   * Process the input filter configurations into a unified structure
   */
  private processFilterConfigurations(): void {
    if (this.checkboxFilterConfig && this.checkboxFilterConfig.length > 0) {
      this.processedFilters = this.checkboxFilterConfig.map((group) => ({
        ...group,
        expanded: group.expanded ?? true,
      }));
    } else {
      this.processedFilters = [];
    }
  }

  private initializeForm(): void {
    const formControls: Record<string, any> = {};
    this.processedFilters.forEach((group) => {
      const checkboxGroup: Record<string, any> = {};
      if (group?.options && Array.isArray(group.options)) {
        group.options.forEach((option) => {
          // Safely check if this option's value is in the applied filters
          const isChecked = this.appliedFilters?.[group?.id]?.includes(
            option?.value
          );
          checkboxGroup[option?.id] = new FormControl(isChecked || false);
        });
      }

      formControls[group?.id] = this.fb.group(checkboxGroup);
    });

    // Create new form with all controls
    const newForm = this.fb.group(formControls);

    // Replace the filterForm with the new form
    this.filterForm = newForm;

    // Only update from applied filters AFTER the form is fully initialized
    if (this.appliedFilters && Object.keys(this.appliedFilters).length > 0) {
      this.updateFormFromAppliedFilters();
    }
    this.filterForm.valueChanges.subscribe((values) => {
      this.checkForActiveFilters();
    });
  }

  private updateFormFromAppliedFilters(): void {
    if (!this.appliedFilters || Object.keys(this.appliedFilters).length === 0)
      return;
    if (!this.filterForm) return;

    // Safer approach: only update controls that exist in both the form and applied filters
    Object.keys(this.appliedFilters).forEach((key) => {
      const control = this.filterForm.get(key);
      if (!control) return;

      // For checkbox groups
      if (Array.isArray(this.appliedFilters[key])) {
        const group = this.processedFilters.find((g) => g.id === key);
        if (!group || group.type !== 'checkbox') return;

        // For each option in the group
        if (group.options && Array.isArray(group.options)) {
          group.options.forEach((option) => {
            // Make sure the option.id exists in the form group
            if (option.id && control.get(option.id)) {
              const isChecked = this.appliedFilters[key].includes(option.value);
              control.get(option.id).setValue(isChecked, { emitEvent: false });
            }
          });
        }
      }
    });
  }

  toggleDrawer(): void {
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.filterForm.reset();
    }
    this.close.emit({ isOpen: this.isOpen });
  }

  toggleGroup(group: FilterGroup): void {
    group.expanded = !group.expanded;
  }

  onSearchChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchSubject.next(value);
  }

  clearAllFilters(): void {
    this.filterForm.reset();
    this.hasActiveFilters = false;
    this.clearAll.emit();
  }

  applyFilters(): void {
    const filterValues: Record<string, any> = {};

    // Process all checkbox filter groups
    this.processedFilters.forEach((group) => {
      const checkboxFormGroup = this.filterForm.get(group.id) as FormGroup;
      if (!checkboxFormGroup) return;

      const selectedValues = [];

      // Get selected checkbox values
      Object.keys(checkboxFormGroup.controls).forEach((key) => {
        if (checkboxFormGroup.get(key)?.value) {
          const option = group.options.find((opt) => opt.id === key);
          if (option?.value !== undefined) {
            selectedValues.push(option.value);
          }
        }
      });

      if (selectedValues.length > 0) {
        filterValues[group.id] = selectedValues;
      }
    });

    this.filtersChanged.emit(filterValues);
    this.toggleDrawer();
  }
}
